import { IReactionDisposer, reaction } from 'mobx';
import { logger } from '@cutwise/utils';

import { FAST_POLLING_INTERVAL } from './Poller.constants';
import { UserPresenceStore } from './UserPresenceStore';

export interface PollingOptions {
  interval: number;
  maxInterval: number;
  shouldStartImmediately: boolean;
}

export const DEFAULT_POLLING_OPTIONS: PollingOptions = {
  interval: FAST_POLLING_INTERVAL,
  maxInterval: FAST_POLLING_INTERVAL,
  shouldStartImmediately: false,
};

export class PollingSubscription {
  private isSubscribed = true;

  private isRequesting = false;

  private currentInterval: number;

  private timeout?: SetTimeout;

  private readonly options: PollingOptions;

  private readonly disposeUserPresenceReaction: IReactionDisposer;

  constructor(
    private readonly userPresenceStore: UserPresenceStore,
    private readonly refresh: () => Promise<boolean | void>,
    options: Partial<PollingOptions> = {},
  ) {
    this.options = { ...DEFAULT_POLLING_OPTIONS, ...options };
    this.currentInterval = this.options.interval;

    this.disposeUserPresenceReaction = reaction(
      () => this.userPresenceStore.isActive,
      (isUserActive) => {
        if (isUserActive) {
          this.currentInterval = this.options.interval;
          this.tick();
        } else {
          clearTimeout(this.timeout);
        }
      },
    );

    if (this.options.shouldStartImmediately) {
      this.tick();
    } else {
      this.schedule();
    }
  }

  unsubscribe() {
    this.isSubscribed = false;

    clearTimeout(this.timeout);
    this.disposeUserPresenceReaction();
  }

  private readonly tick = async () => {
    clearTimeout(this.timeout);

    if (this.isRequesting || !this.canPoll()) {
      return;
    }

    this.isRequesting = true;

    try {
      const hasChanges = await this.refresh();

      this.updateInterval(hasChanges !== false);
    } catch (e) {
      logger.error(e);
      this.updateInterval(false);
    } finally {
      this.isRequesting = false;
    }

    this.schedule();
  };

  private schedule() {
    clearTimeout(this.timeout);

    if (this.canPoll()) {
      this.timeout = setTimeout(this.tick, this.currentInterval);
    }
  }

  private canPoll() {
    return this.isSubscribed && this.userPresenceStore.isActive;
  }

  private updateInterval(hasChanges: boolean) {
    const { interval, maxInterval } = this.options;

    this.currentInterval = hasChanges ? interval : Math.min(this.currentInterval * 2, Math.max(interval, maxInterval));
  }
}
